import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { api } from "@/lib/api";

type Shop = { id: number; code: string; name: string };

type S2aEntry = {
  invoice_no: string;
  date: string | null;
  description: string;
  amount: string;
};

type S2aGroup = {
  industry_code: string;
  industry_name: string;
  order_no: number;
  gtgt_rate: string;
  tncn_rate: string;
  entries: S2aEntry[];
  total: string;
  gtgt_amount: string;
  tncn_amount: string;
};

type S2aReport = {
  meta: {
    ho_kd_name: string;
    address: string;
    tax_code: string;
    location: string;
    representative_name: string;
    currency: string;
    shop_name: string | null;
    period_label: string;
    period_start: string;
    period_end: string;
  };
  groups: S2aGroup[];
  total_revenue: string;
  total_rows: number;
};

type ReportExport = {
  id: number;
  report_code: string;
  period_type: string;
  period_year: number;
  period_value: number;
  shop_id: number | null;
  file_size: number | null;
  sha256: string | null;
  row_count: number | null;
  total_revenue: string | null;
  created_at: string;
};

const fmtVND = (v: string | number | null) =>
  v === null || v === undefined
    ? "—"
    : Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 0 });

const fmtJustDate = (s: string | null) =>
  s
    ? new Date(s + (s.length === 10 ? "T00:00:00" : "")).toLocaleDateString("vi-VN")
    : "—";

const fmtDateTime = (s: string | null) =>
  s ? new Date(s).toLocaleString("vi-VN") : "—";

const thisYear = new Date().getFullYear();
const thisMonth = new Date().getMonth() + 1;

export function SoS2aPage() {
  const [periodType, setPeriodType] = useState<"month" | "quarter" | "year">("month");
  const [year, setYear] = useState(thisYear);
  const [value, setValue] = useState(thisMonth);
  const [shopId, setShopId] = useState<number | "">("");
  const [detailDay, setDetailDay] = useState<string | null>(null);

  const shopsQ = useQuery({
    queryKey: ["shops"],
    queryFn: async () => (await api.get<Shop[]>("/shops")).data,
  });

  const reportQ = useQuery({
    queryKey: ["s2a", periodType, year, value, shopId],
    queryFn: async () => {
      const params: any = { period_type: periodType, year, value };
      if (shopId) params.shop_id = shopId;
      return (await api.get<S2aReport>("/reports/s2a", { params })).data;
    },
  });

  const historyQ = useQuery({
    queryKey: ["report-history"],
    queryFn: async () =>
      (await api.get<ReportExport[]>("/reports/history", { params: { report_code: "S2A" } })).data,
  });

  const valueOptions = useMemo(() => {
    if (periodType === "month") return Array.from({ length: 12 }, (_, i) => i + 1);
    if (periodType === "quarter") return [1, 2, 3, 4];
    return [year];
  }, [periodType, year]);

  const downloadXlsx = async (save: boolean = true) => {
    const params: any = { period_type: periodType, year, value, save };
    if (shopId) params.shop_id = shopId;
    const r = await api.get("/reports/s2a.xlsx", { params, responseType: "blob" });
    const ct = r.headers["content-type"];
    const blob = new Blob([r.data], {
      type:
        typeof ct === "string"
          ? ct
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const periodSlug =
      periodType === "month"
        ? `${String(value).padStart(2, "0")}-${year}`
        : periodType === "quarter"
        ? `Q${value}-${year}`
        : String(year);
    const shopSlug = shopId ? `_shop${shopId}` : "";
    const a = document.createElement("a");
    a.href = url;
    a.download = `S2a-HKD_${periodSlug}${shopSlug}.xlsx`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  const downloadHistory = async (id: number, filename: string) => {
    const r = await api.get(`/reports/history/${id}/download`, { responseType: "blob" });
    const blob = new Blob([r.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Sổ S2a-HKD — Doanh thu
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Báo cáo thuế TT152/2025 theo tháng/quý/năm.
          </p>
        </div>
        <button onClick={() => downloadXlsx(true)} className="btn-primary">
          ⬇ Tải Excel
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Kỳ</label>
          <select
            value={periodType}
            onChange={(e) => {
              const t = e.target.value as any;
              setPeriodType(t);
              if (t === "month") setValue(thisMonth);
              else if (t === "quarter") setValue(Math.ceil(thisMonth / 3));
              else setValue(thisYear);
            }}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="month">Tháng</option>
            <option value="quarter">Quý</option>
            <option value="year">Năm</option>
          </select>
        </div>
        {periodType !== "year" && (
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">
              {periodType === "month" ? "Tháng" : "Quý"}
            </label>
            <select
              value={value}
              onChange={(e) => setValue(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm"
            >
              {valueOptions.map((v) => (
                <option key={v} value={v}>
                  {periodType === "month" ? `Tháng ${v}` : `Quý ${v}`}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Năm</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border rounded px-2 py-1 text-sm w-24"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Cửa hàng</label>
          <select
            value={shopId}
            onChange={(e) => setShopId(e.target.value ? Number(e.target.value) : "")}
            className="border rounded px-2 py-1 text-sm min-w-[12rem]"
          >
            <option value="">— Tất cả —</option>
            {shopsQ.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {reportQ.isLoading && <div className="text-gray-500 text-sm">Đang tải...</div>}
      {reportQ.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
          {(reportQ.error as any)?.response?.data?.detail ?? "Lỗi tải dữ liệu"}
        </div>
      )}

      {reportQ.data && (
        <S2aView report={reportQ.data} onOpenDay={(d) => setDetailDay(d)} />
      )}
      {detailDay && (
        <DayDetailDrawer
          day={detailDay}
          shopId={typeof shopId === "number" ? shopId : null}
          onClose={() => setDetailDay(null)}
        />
      )}

      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-2">Lịch sử xuất</h2>
        <div className="bg-white border rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2">Kỳ</th>
                <th className="text-left px-3 py-2">Cửa hàng</th>
                <th className="text-right px-3 py-2">Số đơn</th>
                <th className="text-right px-3 py-2">Doanh thu</th>
                <th className="text-left px-3 py-2">SHA256</th>
                <th className="text-left px-3 py-2">Xuất lúc</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {historyQ.data?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center text-gray-500">
                    Chưa có file xuất.
                  </td>
                </tr>
              )}
              {historyQ.data?.map((h) => {
                const periodStr =
                  h.period_type === "month"
                    ? `Tháng ${h.period_value}/${h.period_year}`
                    : h.period_type === "quarter"
                    ? `Quý ${h.period_value}/${h.period_year}`
                    : String(h.period_year);
                const shopName =
                  h.shop_id
                    ? shopsQ.data?.find((s) => s.id === h.shop_id)?.code ?? `#${h.shop_id}`
                    : "Tất cả";
                const slug =
                  h.period_type === "month"
                    ? `${String(h.period_value).padStart(2, "0")}-${h.period_year}`
                    : h.period_type === "quarter"
                    ? `Q${h.period_value}-${h.period_year}`
                    : String(h.period_year);
                return (
                  <tr key={h.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{periodStr}</td>
                    <td className="px-3 py-2">{shopName}</td>
                    <td className="px-3 py-2 text-right">{h.row_count}</td>
                    <td className="px-3 py-2 text-right">{fmtVND(h.total_revenue)}đ</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-gray-500">
                      {h.sha256 ? h.sha256.slice(0, 12) + "..." : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {fmtDateTime(h.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() =>
                          downloadHistory(
                            h.id,
                            `${h.report_code}_${slug}.xlsx`
                          )
                        }
                        className="text-xs text-blue-600 hover:underline"
                      >
                        ⬇ Tải
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function S2aView({
  report,
  onOpenDay,
}: {
  report: S2aReport;
  onOpenDay: (date: string) => void;
}) {
  const m = report.meta;
  return (
    <div className="bg-white border rounded overflow-hidden">
      {/* Header block — mimic Excel layout */}
      <div className="grid grid-cols-2 px-6 pt-5 pb-2 text-sm">
        <div>
          <div className="font-semibold">HỘ, CÁ NHÂN KINH DOANH: {m.ho_kd_name || "[Chưa cấu hình]"}</div>
          <div>Địa chỉ: {m.address}</div>
          <div>Mã số thuế: {m.tax_code}</div>
        </div>
        <div className="text-right">
          <div className="font-semibold">Mẫu số S2a-HKD</div>
          <div className="italic text-xs text-gray-500">
            (Kèm theo Thông tư số 152/2025/TT-BTC
            <br />
            ngày 31 tháng 12 năm 2025 của Bộ trưởng Bộ Tài chính)
          </div>
        </div>
      </div>

      <div className="px-6 py-3 text-center">
        <div className="text-xl font-bold tracking-wide">
          SỔ CHI TIẾT DOANH THU BÁN HÀNG HOÁ, DỊCH VỤ
        </div>
        <div className="text-sm mt-2 flex justify-between">
          <span>Địa điểm kinh doanh: {m.location || m.shop_name || ""}</span>
          <span className="italic">Đơn vị tính: {m.currency}</span>
        </div>
        <div className="text-sm">Kỳ kê khai: <b>{m.period_label}</b></div>
      </div>

      <div className="px-6 pb-6 overflow-x-auto">
        <table className="w-full text-xs border border-gray-400 border-collapse">
          <thead>
            <tr>
              <th
                colSpan={2}
                className="border border-gray-400 bg-blue-50 px-2 py-1 font-semibold"
              >
                Chứng từ
              </th>
              <th
                rowSpan={2}
                className="border border-gray-400 bg-blue-50 px-2 py-1 font-semibold"
              >
                Diễn giải
              </th>
              <th
                rowSpan={2}
                className="border border-gray-400 bg-blue-50 px-2 py-1 font-semibold text-right"
              >
                Số tiền
              </th>
            </tr>
            <tr>
              <th className="border border-gray-400 bg-blue-50 px-2 py-1 font-semibold w-24">
                Số hiệu
              </th>
              <th className="border border-gray-400 bg-blue-50 px-2 py-1 font-semibold w-28">
                Ngày, tháng
              </th>
            </tr>
            <tr className="italic text-gray-500">
              <td className="border border-gray-400 px-2 py-0.5 text-center">A</td>
              <td className="border border-gray-400 px-2 py-0.5 text-center">B</td>
              <td className="border border-gray-400 px-2 py-0.5 text-center">C</td>
              <td className="border border-gray-400 px-2 py-0.5 text-center">1</td>
            </tr>
          </thead>
          <tbody>
            {report.groups.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500 border border-gray-400">
                  Không có dữ liệu trong kỳ.
                </td>
              </tr>
            )}
            {report.groups.map((g, gi) => (
              <Group
                key={g.industry_code}
                group={g}
                idx={gi + 1}
                onOpenDay={onOpenDay}
              />
            ))}
            {report.groups.length >= 2 && (
              <tr className="bg-yellow-50 font-semibold">
                <td colSpan={3} className="border border-gray-400 px-2 py-1 text-right">
                  TỔNG CỘNG DOANH THU
                </td>
                <td className="border border-gray-400 px-2 py-1 text-right">
                  {fmtVND(report.total_revenue)}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="mt-6 text-sm text-center">
          <div className="italic">
            Ngày {new Date().getDate()} tháng {new Date().getMonth() + 1} năm{" "}
            {new Date().getFullYear()}
          </div>
          <div className="font-semibold mt-2">
            NGƯỜI ĐẠI DIỆN HỘ KINH DOANH/CÁ NHÂN KINH DOANH
          </div>
          <div className="italic text-xs text-gray-500">
            (Ký, ghi rõ họ tên, đóng dấu (nếu có))
          </div>
          {m.representative_name && (
            <div className="font-semibold mt-12">{m.representative_name}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Group({
  group: g,
  idx,
  onOpenDay,
}: {
  group: S2aGroup;
  idx: number;
  onOpenDay: (date: string) => void;
}) {
  return (
    <>
      <tr className="bg-gray-100">
        <td
          colSpan={4}
          className="border border-gray-400 px-2 py-1 italic font-semibold"
        >
          {idx}. {g.industry_name}
        </td>
      </tr>
      {g.entries.map((e, i) => (
        <tr key={i} className="hover:bg-blue-50">
          <td className="border border-gray-400 px-2 py-0.5 text-center font-mono">
            {e.invoice_no}
          </td>
          <td className="border border-gray-400 px-2 py-0.5 text-center">
            {fmtJustDate(e.date)}
          </td>
          <td
            className="border border-gray-400 px-2 py-0.5 cursor-pointer text-blue-600 hover:underline"
            onClick={() => e.date && onOpenDay(e.date)}
            title="Click để xem chi tiết đơn"
          >
            {e.description}
          </td>
          <td className="border border-gray-400 px-2 py-0.5 text-right">
            {fmtVND(e.amount)}
          </td>
        </tr>
      ))}
      <tr className="bg-gray-50 font-semibold">
        <td colSpan={3} className="border border-gray-400 px-2 py-1 text-right">
          Cộng nhóm {idx}
        </td>
        <td className="border border-gray-400 px-2 py-1 text-right">
          {fmtVND(g.total)}
        </td>
      </tr>
      <tr className="italic">
        <td colSpan={3} className="border border-gray-400 px-2 py-0.5 text-right">
          Thuế GTGT phải nộp ({(Number(g.gtgt_rate) * 100).toFixed(2)}%)
        </td>
        <td className="border border-gray-400 px-2 py-0.5 text-right">
          {fmtVND(g.gtgt_amount)}
        </td>
      </tr>
      <tr className="italic">
        <td colSpan={3} className="border border-gray-400 px-2 py-0.5 text-right">
          Thuế TNCN phải nộp ({(Number(g.tncn_rate) * 100).toFixed(2)}%)
        </td>
        <td className="border border-gray-400 px-2 py-0.5 text-right">
          {fmtVND(g.tncn_amount)}
        </td>
      </tr>
    </>
  );
}

type PosOrderDetail = {
  pos_id: number;
  pos_display_id: number | null;
  carrier: string | null;
  vtp_code: string | null;
  extend_code: string | null;
  customer: string | null;
  phone: string | null;
  address: string | null;
  shop_name: string | null;
  shop_code: string | null;
  pos_cod: number;
  total_price: number;
  bk_sum_cod: number | null;
  bk_sum_fee: number | null;
  bk_sum_received: number | null;
  deliver_date: string | null;
  settlement_codes: string | null;
  revenue_amount: number;
  is_partial: boolean;
  is_settled: boolean;
  received_at: string | null;
  vtp_status: number | null;
  products: { name: string | null; variation: string | null; quantity: number | null; price: number | null }[];
  source: string;
};

type CarrierOnlyDetail = {
  order_code: string;
  carrier: string | null;
  customer: string | null;
  phone: string | null;
  address: string | null;
  cod: number;
  fee: number;
  received: number;
  deliver_date: string | null;
  settlement_code: string | null;
  source: string;
};

type ProductStat = {
  name: string;
  barcode: string;
  qty: number;
  qty_raw: number;
  revenue: number;
  orders: number;
  group_name: string | null;
  unit_label: string;
};

type DayDetailResp = {
  day: string;
  shop_id: number | null;
  pos_orders: PosOrderDetail[];
  carrier_only_orders: CarrierOnlyDetail[];
  product_summary: ProductStat[];
  summary: {
    pos_count: number;
    pos_total: number;
    carrier_count: number;
    carrier_total: number;
    grand_total: number;
    total_orders: number;
    total_qty: number;
    total_variations: number;
  };
};

function DayDetailDrawer({
  day,
  shopId,
  onClose,
}: {
  day: string;
  shopId: number | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["s2a-day-detail", day, shopId],
    queryFn: async () => {
      const params: any = { day };
      if (shopId) params.shop_id = shopId;
      return (
        await api.get<DayDetailResp>("/reports/s2a/day-detail", { params })
      ).data;
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["s2a-day-detail", day, shopId] });
    qc.invalidateQueries({ queryKey: ["s2a"] });
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50" onClick={onClose}>
      <div
        className="bg-white w-full max-w-6xl h-full overflow-auto p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 border-b pb-2">
          <div>
            <h2 className="text-lg font-semibold">
              Chi tiết đơn ngày {fmtJustDate(day)}
            </h2>
            {q.data && (
              <div className="text-sm text-gray-600">
                {q.data.summary.total_orders} đơn / {fmtVND(q.data.summary.grand_total)}đ
                {q.data.summary.carrier_count > 0 && (
                  <span className="ml-2 text-purple-700">
                    (POScake {q.data.summary.pos_count} + tạo tay {q.data.summary.carrier_count})
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={refresh}
              disabled={q.isFetching}
              className="text-xs px-2 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              title="Tải lại sau khi sửa nhóm sản phẩm"
            >
              {q.isFetching ? "⟳ Đang tải..." : "↻ Refresh"}
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-xl">
              ✕
            </button>
          </div>
        </div>

        {q.isLoading && <div className="text-gray-500">Đang tải...</div>}

        {q.data && (
          <>
            {/* Product summary section */}
            {q.data.product_summary.length > 0 && (
              <div className="mb-6">
                <div className="text-sm font-medium mb-2 text-gray-700">
                  📦 THỐNG KÊ SẢN PHẨM ({q.data.summary.total_variations} phân loại
                  POScake, <b>{q.data.summary.total_qty} sản phẩm bán ra</b>)
                </div>

                {/* Group totals — gom theo sản phẩm (= nhóm) */}
                {(() => {
                  const groupTotals: Record<
                    string,
                    { qty: number; orders: number; revenue: number; isRefund: boolean }
                  > = {};
                  for (const p of q.data.product_summary) {
                    const isRefund = p.name.startsWith("Phí Hoàn");
                    const g = isRefund
                      ? "Phí hoàn"
                      : p.group_name || "(Chưa gán nhóm)";
                    if (!groupTotals[g])
                      groupTotals[g] = {
                        qty: 0,
                        orders: 0,
                        revenue: 0,
                        isRefund,
                      };
                    groupTotals[g].qty += p.qty;
                    groupTotals[g].orders += p.orders;
                    groupTotals[g].revenue += p.revenue;
                  }
                  const entries = Object.entries(groupTotals);
                  if (entries.length === 0) return null;
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2 text-xs">
                      <div className="font-semibold text-blue-800 mb-1">
                        Tổng theo sản phẩm:
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                        {entries.map(([gname, t]) => (
                          <div
                            key={gname}
                            className="flex justify-between gap-2 px-2 py-0.5"
                          >
                            <span>
                              <b>{gname}</b>:{" "}
                              {t.isRefund
                                ? `${t.orders} đơn`
                                : `${t.qty} sản phẩm`}{" "}
                              {!t.isRefund && (
                                <span className="text-gray-500">
                                  ({t.orders} đơn)
                                </span>
                              )}
                            </span>
                            <span className="font-mono">{fmtVND(t.revenue)}đ</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <table className="w-full text-xs border border-gray-300">
                  <thead className="bg-purple-50">
                    <tr>
                      <th className="border px-2 py-1 text-left w-8">#</th>
                      <th className="border px-2 py-1 text-left">Sản phẩm (nhóm)</th>
                      <th className="border px-2 py-1 text-left">
                        Phân loại POScake
                      </th>
                      <th className="border px-2 py-1 text-left">Barcode</th>
                      <th
                        className="border px-2 py-1 text-right"
                        title="Số đơn POScake với phân loại này"
                      >
                        Số phân loại
                      </th>
                      <th
                        className="border px-2 py-1 text-right bg-blue-50"
                        title="Số sản phẩm thực (đã nhân hệ số combo của phân loại)"
                      >
                        Sản phẩm thực
                      </th>
                      <th className="border px-2 py-1 text-right">Số đơn</th>
                      <th className="border px-2 py-1 text-right bg-yellow-50">
                        Doanh thu thực
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.product_summary.map((p, i) => (
                      <tr key={p.name} className="hover:bg-purple-50">
                        <td className="border px-2 py-1 text-center text-gray-500">
                          {i + 1}
                        </td>
                        <td className="border px-2 py-1 text-[10px]">
                          {p.group_name ? (
                            <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                              {p.group_name}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="border px-2 py-1 font-medium">{p.name}</td>
                        <td className="border px-2 py-1 text-[10px] text-gray-500">
                          {p.barcode || "—"}
                        </td>
                        <td className="border px-2 py-1 text-right text-gray-700">
                          {p.qty_raw}
                        </td>
                        <td className="border px-2 py-1 text-right font-semibold bg-blue-50">
                          {p.qty}
                          {p.qty !== p.qty_raw && (
                            <span className="text-[9px] text-blue-600 ml-1">
                              (×{(p.qty / p.qty_raw).toFixed(0)})
                            </span>
                          )}
                        </td>
                        <td className="border px-2 py-1 text-right text-gray-600">
                          {p.orders}
                        </td>
                        <td className="border px-2 py-1 text-right font-semibold bg-yellow-50">
                          {fmtVND(p.revenue)}đ
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan={4} className="border px-2 py-1 text-right">
                        TỔNG
                      </td>
                      <td className="border px-2 py-1 text-right">
                        {q.data.product_summary.reduce((s, p) => s + p.qty_raw, 0)}
                      </td>
                      <td className="border px-2 py-1 text-right bg-blue-100">
                        {q.data.summary.total_qty}
                      </td>
                      <td className="border px-2 py-1"></td>
                      <td className="border px-2 py-1 text-right bg-yellow-100">
                        {fmtVND(
                          q.data.product_summary.reduce((s, p) => s + p.revenue, 0)
                        )}đ
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* POScake orders table */}
            {q.data.pos_orders.length > 0 && (
              <div className="mb-6">
                <div className="text-sm font-medium mb-2 text-gray-700">
                  ĐƠN POSCAKE ({q.data.summary.pos_count} đơn / {fmtVND(q.data.summary.pos_total)}đ)
                </div>
                <table className="w-full text-xs border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border px-2 py-1 text-left">#</th>
                      <th className="border px-2 py-1 text-left">Mã vận đơn</th>
                      <th className="border px-2 py-1 text-left">ĐVVC</th>
                      <th className="border px-2 py-1 text-left">Khách hàng</th>
                      <th className="border px-2 py-1 text-left">Shop</th>
                      <th className="border px-2 py-1 text-left">Sản phẩm</th>
                      <th className="border px-2 py-1 text-right">POS COD</th>
                      <th className="border px-2 py-1 text-right">BK thực thu</th>
                      <th className="border px-2 py-1 text-right">Phí</th>
                      <th className="border px-2 py-1 text-right">Thu về</th>
                      <th className="border px-2 py-1 text-right bg-yellow-50">DT ghi sổ</th>
                      <th className="border px-2 py-1 text-left">Ngày giao</th>
                      <th className="border px-2 py-1 text-left">Bảng kê</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.pos_orders.map((o, i) => (
                      <tr key={o.pos_id} className="hover:bg-blue-50">
                        <td className="border px-2 py-1 text-center text-gray-500">
                          {i + 1}
                        </td>
                        <td className="border px-2 py-1 font-mono">
                          <div>{o.vtp_code}</div>
                          {o.extend_code && o.extend_code !== o.vtp_code && (
                            <div className="text-[10px] text-gray-500">
                              {o.extend_code}
                            </div>
                          )}
                        </td>
                        <td className="border px-2 py-1">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] ${
                              o.carrier?.includes("JT")
                                ? "bg-red-100 text-red-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {o.carrier ?? "—"}
                          </span>
                        </td>
                        <td className="border px-2 py-1">
                          <div>{o.customer ?? "—"}</div>
                          <div className="text-[10px] text-gray-500">{o.phone}</div>
                          {o.address && (
                            <div
                              className="text-[10px] text-gray-400 truncate max-w-[15rem]"
                              title={o.address}
                            >
                              {o.address}
                            </div>
                          )}
                        </td>
                        <td className="border px-2 py-1 text-[10px]">{o.shop_name}</td>
                        <td className="border px-2 py-1">
                          {o.products.length === 0 ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            <div className="space-y-0.5">
                              {o.products.map((p, j) => (
                                <div key={j} className="text-[10px]">
                                  • {p.name || p.variation || "?"}
                                  {p.quantity && p.quantity > 1 && (
                                    <span className="text-gray-500"> × {p.quantity}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="border px-2 py-1 text-right">
                          {fmtVND(o.pos_cod)}
                        </td>
                        <td className="border px-2 py-1 text-right">
                          {o.bk_sum_cod === null ? "—" : fmtVND(o.bk_sum_cod)}
                        </td>
                        <td className="border px-2 py-1 text-right text-gray-600">
                          {o.bk_sum_fee === null ? "—" : fmtVND(o.bk_sum_fee)}
                        </td>
                        <td
                          className={`border px-2 py-1 text-right ${
                            o.bk_sum_received !== null && o.bk_sum_received < 0
                              ? "text-red-600"
                              : ""
                          }`}
                        >
                          {o.bk_sum_received === null ? "—" : fmtVND(o.bk_sum_received)}
                        </td>
                        <td className="border px-2 py-1 text-right font-semibold bg-yellow-50">
                          {fmtVND(o.revenue_amount)}
                        </td>
                        <td className="border px-2 py-1 text-[10px]">
                          {o.deliver_date ? fmtDateTime(o.deliver_date) : "—"}
                        </td>
                        <td className="border px-2 py-1 text-[10px] font-mono">
                          {o.settlement_codes ?? (
                            <span className="text-orange-600">Chờ bảng kê</span>
                          )}
                          {o.is_partial && (
                            <div className="text-purple-700">🔄 1 phần</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Carrier-only orders */}
            {q.data.carrier_only_orders.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-medium mb-2 text-gray-700">
                  ĐƠN TẠO TAY VTP/JT ({q.data.summary.carrier_count} đơn /{" "}
                  {fmtVND(q.data.summary.carrier_total)}đ)
                </div>
                <table className="w-full text-xs border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border px-2 py-1">Mã vận đơn</th>
                      <th className="border px-2 py-1">ĐVVC</th>
                      <th className="border px-2 py-1">Khách</th>
                      <th className="border px-2 py-1 text-right">COD</th>
                      <th className="border px-2 py-1 text-right">Phí</th>
                      <th className="border px-2 py-1 text-right">Thu về</th>
                      <th className="border px-2 py-1">Bảng kê</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.carrier_only_orders.map((o) => (
                      <tr key={o.order_code} className="hover:bg-gray-50">
                        <td className="border px-2 py-1 font-mono">{o.order_code}</td>
                        <td className="border px-2 py-1">{o.carrier}</td>
                        <td className="border px-2 py-1">
                          <div>{o.customer ?? "—"}</div>
                          <div className="text-[10px] text-gray-500">{o.phone}</div>
                        </td>
                        <td className="border px-2 py-1 text-right">{fmtVND(o.cod)}</td>
                        <td className="border px-2 py-1 text-right">{fmtVND(o.fee)}</td>
                        <td className="border px-2 py-1 text-right">{fmtVND(o.received)}</td>
                        <td className="border px-2 py-1 text-[10px] font-mono">
                          {o.settlement_code}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
